import { Button, Select, SelectOptionType } from '@bp/ui-components';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from '../../../utils/stringHelper';
import {
  mandatoryStudentMappingOptions,
  StudenMappingSelectType,
  StudentMapping,
  studentMappingOptions,
  XlsxFileImport,
  XlsxImportColumn,
} from '../types/XlsxImportTypes';
import { getLabelFromMapping, getStringFromEntry } from '../utils/XlsxImportUtils';
import styles from './_XlsxImportForms.module.scss';

type XlsxStudentImportFormProps = {
  fileImport: XlsxFileImport;
  initialColumns: XlsxImportColumn<StudentMapping>[];
  onColumnsChanged: (columns: XlsxImportColumn<StudentMapping>[]) => void;
};

export const XlsxStudentImportForm = ({ fileImport, initialColumns, onColumnsChanged }: XlsxStudentImportFormProps) => {
  const { t } = useTranslation();

  const [columns, setColumns] = useState<XlsxImportColumn<StudentMapping>[]>(initialColumns);
  const [rows, setRows] = useState<(string | null)[][]>(fileImport.rows.slice(0, 5));

  const columnOptions: StudenMappingSelectType[] = useMemo(
    () =>
      studentMappingOptions
        .map((option) => ({
          label: getLabelFromMapping(option),
          value: option,
        }))
        .sort((a, b) => naturalStringSort(a.label, b.label)),
    [],
  );

  const remainingColumnOptions: StudenMappingSelectType[] = useMemo(() => {
    const mandatory = columnOptions
      .filter(
        (option) =>
          columns.some((c) => c.content === option.value && c.index === null) &&
          mandatoryStudentMappingOptions.includes(option.value),
      )
      .map((option) => ({
        label: option.label,
        value: option.value,
        group: t('import.mandatoryFields'),
      }));
    const optional = columnOptions
      .filter((option) => columns.some((c) => c.content === option.value && c.index === null))
      .map((option) => ({
        label: option.label,
        value: option.value,
        group: t('import.optionalFields'),
      }));

    return [...mandatory, ...optional];
  }, [columnOptions, columns, t]);

  useEffect(() => {
    setColumns(initialColumns);
  }, [initialColumns]);

  return (
    <div className={styles['xlsx-import-form']}>
      <div className={styles.table}>
        <table>
          <thead>
            <tr>
              {fileImport.headers.map((h, index) => {
                return (
                  <th key={h}>
                    {
                      <Select
                        options={remainingColumnOptions}
                        hasGroups
                        value={columnOptions.find((option) => {
                          const mappingItem = columns.find((c) => c.index === index);
                          return mappingItem && option.value === mappingItem.content;
                        })}
                        onChange={async (e) => {
                          const selectedValue = e ? ((e as SelectOptionType).value as StudentMapping) : null;

                          // Clear any existing mapping for this column
                          const updatedValues = columns.map((item) =>
                            item.index === index ? { ...item, index: null } : item,
                          );

                          // If a value is selected, find the mapping item and set the column index
                          if (selectedValue) {
                            const mappingIndex = updatedValues.findIndex((item) => item.content === selectedValue);
                            if (mappingIndex !== -1) {
                              updatedValues[mappingIndex].index = index;
                            }
                          }

                          // Update the entire form values array
                          setColumns(updatedValues);
                          onColumnsChanged(updatedValues);
                        }}
                        name={`column-${index}`}
                        dense
                        isClearable
                        placeholder={t('import.ignoreColumn')}
                        menuPosition='fixed'
                      />
                    }
                  </th>
                );
              })}
            </tr>
            <tr>
              {fileImport.headers.map((h) => {
                return <th key={h}>{h}</th>;
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((entry, entryIndex) => {
                  return <td key={`${rowIndex}-${entryIndex}`}>{getStringFromEntry(entry)}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className={styles.toggle}>
          {rows.length !== fileImport.rows.length && (
            <div className={styles.hint}>{t('import.tableHint', { rowCount: fileImport.rows.length })}</div>
          )}
          <Button
            hierarchy='secondary'
            onClick={() =>
              rows.length === fileImport.rows.length ? setRows(fileImport.rows.slice(0, 5)) : setRows(fileImport.rows)
            }
          >
            {rows.length === fileImport.rows.length ? t('common.showLess') : t('common.showAll')}
          </Button>
        </div>
      </div>
    </div>
  );
};
